<template>
  <div class="theme-picker__wrapper">
    <div
      class="theme-picker"
      :class="isOpen && 'theme-picker--open'"
    >
      <div
        class="theme-picker__dropdown-wrapper"
        ref="dropdownWrapper"
      >
        <ThemePickerCard
          class="theme-picker__preview"
          :theme-group="selectedThemeGroup"
        />
        <FocusTrap
          :active="isOpen"
          initial-focus="button"
          fallback-focus="button"
          @deactivate="isOpen = false"
        >
          <div
            v-show="isOpen"
            :aria-hidden="(!isOpen).toString()"
            class="theme-picker__dropdown"
            :style="{ maxHeight: `calc(100vh - ${dropdownOffset}px)` }"
            role="region"
            :aria-label="$t('Themes dropdown')"
            tabindex="-1"
          >
            <ThemePickerCard
              v-for="themeGroup in themeGroups"
              :key="themeGroup.id"
              class="theme-picker__card"
              :theme-group="themeGroup"
            />
          </div>
        </FocusTrap>
      </div>
      <NebulaButton
        class="theme-picker__toggle-button"
        :icon-left="isOpen ? 'caret-up' : 'caret-down'"
        :aria-expanded="isOpen.toString()"
        :aria-label="$t('Choose Theme')"
        type="flat"
        @click="toggleOpen"
      />
    </div>
    <NebulaButton
      class="theme-picker__apply-all-button"
      aria-describedby="theme-picker__apply-to-all-aria-description"
      size="s"
      type="ghost"
      :text="$t('Apply to All')"
      @click="applyThemeToAll"
    />
    <span
      id="theme-picker__apply-to-all-aria-description"
      class="theme-picker__aria-description"
    >
      {{ draftType === 'board'
        ? $t('Apply the current page theme to all pages')
        : $t('Apply the current slide theme to all slides') }}
    </span>
  </div>
</template>

<script>
import { NebulaButton } from '@discoveryedu/nebula-components';
import { FocusTrap } from 'focus-trap-vue';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import ThemePickerCard from '@/components/ThemePickerCard.vue';
import { themes } from '@/lib/constants/themes.json';
import { bus } from '@/lib/eventBus';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'ThemePicker',
  components: {
    NebulaButton,
    FocusTrap,
    ThemePickerCard,
  },
  data() {
    return {
      dropdownOffset: null,
      isOpen: false,
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draftType',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    pageTheme() {
      return get(this, 'currentPage.options.theme', {});
    },
    selectedThemeGroup() {
      return this.themeGroups.find((tg) => tg.id === this.pageTheme.name)
        || this.themeGroups[0]; // for old boards, fall back to the default theme
    },
    themeGroups() {
      // Editorial themes are only available for editorial users
      return themes.filter((theme) => (this.isDeUser || !theme.editorialOnly));
    },
  },
  beforeUnmount() {
    bus.off('document:mousedown', this.onBodyClick);
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_ALL_PAGE_OPTIONS,
    ]),
    applyThemeToAll() {
      this[types.UPDATE_ALL_PAGE_OPTIONS]({
        options: {
          theme: {
            name: this.pageTheme.name || 'default',
            variation: this.pageTheme.variation || 1,
          },
        },
      });
    },
    onBodyClick({ target }) {
      if (!this.$el.contains(target)) {
        this.isOpen = false;
      }
    },
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
  },
  watch: {
    isOpen(open) {
      if (open && this.$refs.dropdownWrapper) {
        // Since the dropdown is absolutely positioned, prevent it from running off
        // the bottom of the viewport
        this.dropdownOffset = this.$refs.dropdownWrapper.getBoundingClientRect().y + 16;
        bus.on('document:mousedown', this.onBodyClick);
      } else {
        bus.off('document:mousedown', this.onBodyClick);
      }
    },
  },
};
</script>

<style lang="stylus">
.theme-picker {
  display: flex;
  flex-shrink: 0;
  margin: 0 0 $nebula-space-2x 0;

  &__wrapper {
    padding: $nebula-space-2x;
  }

  &__dropdown-wrapper {
    flex-grow: 1;
    position: relative;
    z-index: 1;
  }

  &__preview {
    .theme-picker--open & {
      box-shadow: none;
    }
  }

  &__aria-description {
    display: none;
  }

  &__dropdown {
    background-color: $nebula-color-white;
    border-radius: $nebula-border-radius-badge-default;
    box-shadow: $comet-shadow-z-shadow-2;
    left: 0;
    overflow: auto;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__card:not(:last-child) {
    margin: 0 0 $nebula-space-2x 0;
  }

  &__toggle-button, &__apply-all-button {
    margin: 0;
  }

  html[dir="rtl"] & {
    &__toggle-button, &__apply-all-button {
      margin: 0;
    }
  }
}
</style>
